import html2canvas from 'html2canvas';
import { buildDocumentExportMetaTitleCaptureCss } from './documentExportMetaTitleFonts';

/**
 * PDF/프린트 다크·라이트 모드 공통 규칙
 *
 * 1. document.documentElement 테마를 바꾸지 않는다 (화면 깜빡임 방지).
 * 2. 모달 캡처: createDocumentExportCaptureShell + scopeCaptureCssToExportShell.
 *    셸 밖 <style>은 문서 전역에 적용되므로 반드시 셸 클래스로 스코프한다.
 * 3. 화면 직접 캡처: captureVisibleElementForExport (단순 DOM만 — 복잡 표는 모달 블록 캡처 사용).
 * 4. 색상은 캡처 CSS·셸 라이트 오버라이드로, 레이아웃만 DOCUMENT_EXPORT_INLINE_STYLE_OPTIONS 로 복사.
 * 5. 출력물은 항상 라이트 모드(인쇄용) 외관.
 */

export const DOCUMENT_EXPORT_CAPTURE_SHELL_CLASS = 'cp-document-export-capture-shell';

export const DOCUMENT_EXPORT_CAPTURE_SETTLE_MS = 180;

export const DOCUMENT_EXPORT_CAPTURE_SCALES = [3, 2, 1] as const;

const SHELL_SELECTOR = `html body .${DOCUMENT_EXPORT_CAPTURE_SHELL_CLASS}`;

export type DocumentExportCloneCustomizer = (
  clonedDoc: Document,
  clonedElement: HTMLElement
) => void;

/** 다크 모드 :root 규칙을 이기기 위한 캡처 셸 전용 라이트 테마·오버라이드 */
export function buildDocumentExportCaptureShellThemeCss(): string {
  return `
.${DOCUMENT_EXPORT_CAPTURE_SHELL_CLASS} {
  color: #1f1f1f;
  --color-bg: #f3f3f5;
  --color-panel: #ffffff;
  --color-surface: #ffffff;
  --color-border: #d8dde4;
  --color-section-bg: #e5e5e5;
  --color-text-primary: #1f1f1f;
  --color-text-secondary: #5e646e;
  --color-accent-strong: #10504a;
  --color-accent: #0d4d44;
  --color-success: #2e7d32;
}

${SHELL_SELECTOR} .cp-weekly-learning-guide-table {
  --wlg-edge-color: #8b8d96 !important;
  --wlg-title-bg: #e8e8eb !important;
  background: #ffffff !important;
  color: #1f1f1f !important;
}

${SHELL_SELECTOR} .cp-weekly-learning-guide-table th,
${SHELL_SELECTOR} .cp-weekly-learning-guide-table td {
  border-color: #d8dde4 !important;
  color: #1f1f1f !important;
}

${SHELL_SELECTOR} .cp-weekly-learning-guide-cell.cp-weekly-learning-guide-cell--neis-match-base {
  background: #ffffff !important;
}

${SHELL_SELECTOR} .cp-weekly-learning-guide-cell.cp-weekly-learning-guide-cell--neis-diff-track {
  background: #e2e2ea !important;
}

${SHELL_SELECTOR} .cp-weekly-learning-guide-cell.cp-weekly-learning-guide-cell--neis-dedicated-mismatch {
  background: #fff6cc !important;
}

${SHELL_SELECTOR} .cp-weekly-learning-guide-cell--neis-unregistered-timetable {
  color: #d32f2f !important;
  font-weight: 800;
}

${SHELL_SELECTOR} .cp-weekly-learning-guide-cell.cp-weekly-learning-guide-cell--surface-holiday {
  background: rgba(211, 47, 47, 0.22) !important;
}

${SHELL_SELECTOR} .cp-weekly-learning-guide-table td.cp-weekly-learning-guide-cell.cp-weekly-cell-no-class.cp-weekly-cell-unused-kind {
  background: repeating-linear-gradient(
    -45deg,
    #ffffff,
    #ffffff 2px,
    rgba(128, 128, 128, 0.12) 2px,
    rgba(128, 128, 128, 0.12) 4px
  ) !important;
}

${SHELL_SELECTOR} .cp-weekly-learning-guide-table td.cp-weekly-learning-guide-cell.cp-weekly-cell-activity-hatch {
  background-color: #f5f0e6 !important;
  background-image: repeating-linear-gradient(
    -45deg,
    transparent,
    transparent 2px,
    rgba(255, 255, 255, 0.72) 2px,
    rgba(255, 255, 255, 0.72) 4px
  ) !important;
}

${SHELL_SELECTOR} .cp-annual-cross-topic-detail-table th,
${SHELL_SELECTOR} .cp-annual-cross-topic-detail-table td {
  color: #111 !important;
  border-color: #d8d8d8 !important;
}

${SHELL_SELECTOR} .cp-annual-cross-topic-detail-table th {
  background: #f4f4f4 !important;
}

${SHELL_SELECTOR} .cp-annual-cross-topic-detail-table th span,
${SHELL_SELECTOR} .cp-annual-cross-topic-detail-table td span {
  border: none !important;
  box-shadow: none !important;
}

${SHELL_SELECTOR} .cp-annual-cross-topic-detail-period-value {
  display: block !important;
  width: 100% !important;
  text-align: center !important;
}

${SHELL_SELECTOR} .cp-annual-cross-topic-detail-table td {
  background: #ffffff !important;
}

${SHELL_SELECTOR} .cp-annual-cross-topic-detail-table tbody tr:nth-child(even) td {
  background: #f8f8f8 !important;
}

${SHELL_SELECTOR} .cp-annual-operation-plan-table th,
${SHELL_SELECTOR} .cp-annual-cross-topic-detail-table th {
  background: #f4f4f4 !important;
  color: #1f1f1f !important;
}

${SHELL_SELECTOR} .cp-annual-operation-plan-table .cp-annual-operation-plan-class-head-empty {
  color: #5e646e !important;
}

${SHELL_SELECTOR} .cp-annual-operation-plan-day-inactive {
  background: rgba(148, 163, 184, 0.2) !important;
}

${SHELL_SELECTOR} .cp-annual-operation-plan-day-closure {
  background: rgba(255, 152, 0, 0.22) !important;
}

${SHELL_SELECTOR} .cp-annual-operation-plan-day-cell.cp-annual-operation-plan-day-memo-general {
  color: #5a8a84 !important;
}

${SHELL_SELECTOR} .cp-annual-operation-plan-activity-daykind {
  color: #d32f2f !important;
}

${SHELL_SELECTOR} .cp-annual-operation-plan-activity-line.cp-annual-operation-plan-activity-memo-general {
  color: #1f1f1f !important;
}

${SHELL_SELECTOR} .cp-annual-operation-plan-activity-line.cp-annual-operation-plan-activity-memo-general .cp-annual-operation-plan-activity-tag {
  color: #1f1f1f !important;
}

${SHELL_SELECTOR} .cp-annual-operation-plan-activity-tag {
  color: #1f1f1f !important;
}

${SHELL_SELECTOR} .cp-annual-cross-topic-detail-scope--term1 {
  background: #f3ecee !important;
  color: #4a383d !important;
  border-color: rgba(74, 56, 61, 0.22) !important;
}

${SHELL_SELECTOR} .cp-annual-cross-topic-detail-scope--term2 {
  background: #ebf0f4 !important;
  color: #2c3a45 !important;
  border-color: rgba(44, 58, 69, 0.22) !important;
}

${SHELL_SELECTOR} .cp-annual-cross-topic-detail-scope--annual {
  background: #f7ede4 !important;
  color: #5a3d26 !important;
  border-color: rgba(90, 61, 38, 0.22) !important;
}
`;
}

/** 캡처 셸 내부 <style>은 문서 전역에 적용되므로 셸 클래스로 한정 */
export function scopeCaptureCssToExportShell(captureCss: string): string {
  const shellClass = DOCUMENT_EXPORT_CAPTURE_SHELL_CLASS;
  return captureCss.replace(/(^|})\s*([^@{}/][^{]*)\{/g, (_, after, selectorPart) => {
    const trimmed = selectorPart.trim();
    if (!trimmed) {
      return `${after} ${selectorPart}{`;
    }
    const scoped = trimmed
      .split(',')
      .map((selector: string) => {
        const sel = selector.trim();
        if (!sel) return sel;
        return `.${shellClass} ${sel}`;
      })
      .join(', ');
    return `${after}\n${scoped} {`;
  });
}

export function buildDocumentExportCloneFixesCss(): string {
  return `
.cp-weekly-learning-guide-notice-textarea {
  display: none !important;
}
.cp-weekly-learning-guide-notice-export-text {
  display: block !important;
}
.cp-weekly-learning-guide-table-wrap {
  overflow: visible !important;
}
.cp-weekly-learning-guide-cell-learning-inner {
  display: block !important;
  overflow: visible !important;
  -webkit-line-clamp: unset !important;
  line-clamp: unset !important;
  -webkit-box-orient: unset !important;
}
`;
}

export function createDocumentExportCaptureShell(widthPx: number, captureCss: string): HTMLDivElement {
  const shell = document.createElement('div');
  shell.className = DOCUMENT_EXPORT_CAPTURE_SHELL_CLASS;
  shell.setAttribute('data-theme', 'light');
  shell.setAttribute('aria-hidden', 'true');
  shell.style.position = 'fixed';
  shell.style.left = '-9999px';
  shell.style.top = '0';
  shell.style.width = `${widthPx}px`;
  shell.style.background = '#ffffff';
  shell.style.pointerEvents = 'none';
  shell.style.overflow = 'visible';

  const themeStyle = document.createElement('style');
  themeStyle.textContent = buildDocumentExportCaptureShellThemeCss();
  shell.appendChild(themeStyle);

  const captureStyle = document.createElement('style');
  captureStyle.textContent = scopeCaptureCssToExportShell(captureCss);
  shell.appendChild(captureStyle);

  document.body.appendChild(shell);
  return shell;
}

/** html2canvas 클론 문서 전용 — 화면 UI에는 영향 없음 */
export function applyLightThemeToCloneDocument(clonedDoc: Document): void {
  clonedDoc.documentElement.setAttribute('data-theme', 'light');
}

/** html2canvas가 본문 위에 겹치는 클론을 화면 밖으로 이동 */
export function moveHtml2canvasCloneOffScreen(clonedDoc: Document, clonedElement: HTMLElement): void {
  let wrapper: HTMLElement = clonedElement;
  while (wrapper.parentElement && wrapper.parentElement !== clonedDoc.body) {
    wrapper = wrapper.parentElement;
  }

  clonedDoc.body.style.margin = '0';
  clonedDoc.body.style.background = '#ffffff';

  wrapper.style.position = 'fixed';
  wrapper.style.left = '-12000px';
  wrapper.style.top = '0';
  wrapper.style.zIndex = '-1';
  wrapper.style.pointerEvents = 'none';
  wrapper.style.opacity = '1';
  wrapper.style.visibility = 'visible';
}

export function injectStylesIntoCloneDocument(clonedDoc: Document, css: string): void {
  const style = clonedDoc.createElement('style');
  style.textContent = css;
  clonedDoc.head.appendChild(style);
}

/** html2canvas iframe 클론에 복사된 App.css 등 — color-mix 파싱 오류 원인 */
export function stripDocumentExportStylesheetsFromClone(clonedDoc: Document): void {
  clonedDoc.querySelectorAll('link[rel="stylesheet"], style').forEach((node) => {
    node.parentNode?.removeChild(node);
  });
}

export function createDocumentExportOnclone(
  customizeClone?: DocumentExportCloneCustomizer,
  options?: { moveCloneOffScreen?: boolean }
): (clonedDoc: Document, clonedElement: HTMLElement) => void {
  const moveCloneOffScreen = options?.moveCloneOffScreen ?? true;
  return (clonedDoc, clonedElement) => {
    applyLightThemeToCloneDocument(clonedDoc);
    if (moveCloneOffScreen) {
      moveHtml2canvasCloneOffScreen(clonedDoc, clonedElement);
    }
    customizeClone?.(clonedDoc, clonedElement);
  };
}

/** html2canvas 클론에는 셸 형제 <style>이 포함되지 않으므로 onclone에서 캡처 CSS를 다시 주입 */
export function createShellCaptureOnclone(captureCss?: string): DocumentExportCloneCustomizer {
  return (clonedDoc, clonedElement) => {
    if (captureCss) {
      injectStylesIntoCloneDocument(clonedDoc, captureCss);
    }
    injectStylesIntoCloneDocument(clonedDoc, buildDocumentExportMetaTitleCaptureCss());
    injectStylesIntoCloneDocument(clonedDoc, buildDocumentExportCloneFixesCss());

    let node: HTMLElement | null = clonedElement;
    while (node && node !== clonedDoc.body) {
      node.classList.add(DOCUMENT_EXPORT_CAPTURE_SHELL_CLASS);
      node.setAttribute('data-theme', 'light');
      node = node.parentElement;
    }
  };
}

/** 화면에 보이는 요소 캡처 — onclone에서만 라이트 테마·스타일 적용 */
export async function captureVisibleElementForExport(
  element: HTMLElement,
  options?: {
    settleMs?: number;
    scrollIntoView?: boolean;
    customizeClone?: DocumentExportCloneCustomizer;
  }
): Promise<HTMLCanvasElement> {
  if (options?.scrollIntoView !== false) {
    element.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  await new Promise((resolve) => {
    setTimeout(resolve, options?.settleMs ?? DOCUMENT_EXPORT_CAPTURE_SETTLE_MS);
  });

  const onclone = createDocumentExportOnclone(options?.customizeClone, { moveCloneOffScreen: true });

  for (const scale of DOCUMENT_EXPORT_CAPTURE_SCALES) {
    try {
      const canvas = await html2canvas(element, {
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        scale,
        onclone,
      });
      if (canvas.height >= 2 && canvas.width >= 2) {
        return canvas;
      }
    } catch {
      // scale 재시도
    }
  }

  throw new Error('캡처 이미지 생성에 실패했습니다.');
}
